import { getConnectorRootURL } from '@/config'
import { getCurrentUserIdToken } from '@/services/auth'

import { composedFetch } from './helpers'
import { errorIfInvalidResponse } from './errors'

import {
  getInvitesSchema,
  ApiInvite,
  ApiTeam,
  getTeamsSchema,
  captureInvalidSchemaOnSentry,
} from '@/api/schemas'

//      _            _ _
//     (_)          (_) |
//      _ _ ____   ___| |_ ___  ___
//     | | '_ \ \ / / | __/ _ \/ __|
//     | | | | \ V /| | ||  __/\__ \
//     |_|_| |_|\_/ |_|\__\___||___/

function getInvitesEndpointURL(): string {
  return getConnectorRootURL() + '/invites'
}

function getAcceptInviteURL(hash: string): string {
  return getInvitesEndpointURL() + `/accept/${hash}`
}

function getCancelInviteURL(inviteUuid: string): string {
  return getInvitesEndpointURL() + `/${inviteUuid}`
}

export async function apiGetInvites(): Promise<ApiInvite[]> {
  const response = await composedFetch(
    'GET',
    getInvitesEndpointURL(),
    await getCurrentUserIdToken(),
  )

  await errorIfInvalidResponse(response, 'Failed to get invites.')

  const json = await response.json()

  captureInvalidSchemaOnSentry(json, getInvitesSchema)

  return json.user_invites
}

export async function apiInviteTeamMember(email: string): Promise<void> {
  const response = await composedFetch(
    'POST',
    getInvitesEndpointURL(),
    await getCurrentUserIdToken(),
    { invitee_email: email },
  )

  await errorIfInvalidResponse(response, 'Failed to send an invite to the team.')
}

export async function apiAcceptInvite(secret: string): Promise<void> {
  const response = await composedFetch('GET', getAcceptInviteURL(secret))

  await errorIfInvalidResponse(response, 'Failed to get invites.')
}

export async function apiCancelInvite(inviteUuid: string): Promise<void> {
  const response = await composedFetch(
    'DELETE',
    getCancelInviteURL(inviteUuid),
    await getCurrentUserIdToken(),
  )

  await errorIfInvalidResponse(response, 'Failed to delete team member.')
}

//                               _
//                              | |
//      _ __ ___   ___ _ __ ___ | |__   ___ _ __ ___
//     | '_ ` _ \ / _ \ '_ ` _ \| '_ \ / _ \ '__/ __|
//     | | | | | |  __/ | | | | | |_) |  __/ |  \__ \
//     |_| |_| |_|\___|_| |_| |_|_.__/ \___|_|  |___/

function getTeamsEndpointURL(): string {
  return getConnectorRootURL() + '/teams'
}

function getRemoveMemberURL(teamUuid: string, memberUuid: string): string {
  return getTeamsEndpointURL() + `/${teamUuid}/members/${memberUuid}`
}

export async function apiGetTeams(): Promise<ApiTeam[]> {
  const response = await composedFetch('GET', getTeamsEndpointURL(), await getCurrentUserIdToken())

  await errorIfInvalidResponse(response, 'Failed to get invites.')

  const json = await response.json()

  captureInvalidSchemaOnSentry(json, getTeamsSchema)

  return json.user_teams
}

export async function apiRemoveMember(teamUuid: string, memberUuid: string): Promise<void> {
  const response = await composedFetch(
    'DELETE',
    getRemoveMemberURL(teamUuid, memberUuid),
    await getCurrentUserIdToken(),
  )

  await errorIfInvalidResponse(response, 'Failed to delete team member.')
}

//       _
//      | |
//   ___| |__   __ _ _ __ ___   _ __ ___  ___  ___  _   _ _ __ ___ ___  ___
//  / __| '_ \ / _` | '__/ _ \ | '__/ _ \/ __|/ _ \| | | | '__/ __/ _ \/ __|
//  \__ \ | | | (_| | | |  __/ | | |  __/\__ \ (_) | |_| | | | (_|  __/\__ \
//  |___/_| |_|\__,_|_|  \___| |_|  \___||___/\___/ \__,_|_|  \___\___||___/

type ResourceType = 'workstation' | 'drive'

function getSharedResourceUrl(type: ResourceType, uuid: string): string {
  const resource = type === 'workstation' ? type : 'drive'
  return getConnectorRootURL() + `/share_${resource}s/${uuid}/`
}

/**
 * Get list of user uuids for a given resource
 * @param type of resource
 * @param uuid of the particular resource
 * @returns a raw, list of api members
 */
export async function apiGetShareeUuidsForResource(
  type: ResourceType,
  uuid: string,
): Promise<string[]> {
  const response = await composedFetch(
    'GET',
    getSharedResourceUrl(type, uuid),
    await getCurrentUserIdToken(),
  )

  await errorIfInvalidResponse(response, 'Failed to load information about sharees.')

  const json = await response.json()

  return json
}
